'use client'

import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'

/**
 * Reports the org id carried by the live Convex JWT (or `undefined`
 * while the whoami query is still loading / auth is bootstrapping,
 * `null` for a personal / org-less session).
 *
 * Backed by `api.identity.queries.getCurrentOrgId`, which is guarded
 * only by `requireAuth` and therefore never throws on an org mismatch.
 * Convex dedups identical query subscriptions, so calling this hook
 * from multiple places shares a single websocket subscription.
 *
 * Use it to detect the transient Clerk-org-switch window where the
 * server-seeded `orgId` prop and the JWT's org claim disagree — see
 * `components/layout/org-auth-switch-boundary.tsx` and the brand-list
 * gate in `contexts/brand-context.tsx`.
 */
export function useConvexOrgId(): string | null | undefined {
  const result = useAuthedQuery(api.identity.queries.getCurrentOrgId, {})
  if (result === undefined) {
    return
  }
  return result.orgId
}

/**
 * Pure predicate: does the live Convex JWT org claim (`convexOrgId`)
 * authorize the given `orgId`? True only when `orgId` is present AND the
 * token's claim matches it exactly.
 *
 * Extracted so the gate can be unit-tested without React, and shared by
 * every org-scoped client query that must hold until the token settles.
 */
export function isConvexOrgReady(
  convexOrgId: string | null | undefined,
  orgId: string | null | undefined
): boolean {
  return Boolean(orgId) && convexOrgId === orgId
}

/**
 * True only when the live Convex JWT org claim equals `orgId`.
 *
 * Use it to gate org-scoped `useAuthedQuery` calls so they NEVER fire
 * with an `orgId` the current token can't authorize — the transient
 * Clerk-org-switch window where the server-seeded `orgId` prop and the
 * JWT claim disagree. Without this, such a query throws `NOT_AUTHORIZED`
 * during render and trips `OrgAuthSwitchBoundary` (a recoverable but
 * UI-disrupting full-shell fallback). Mirrors the brand-list gate in
 * `contexts/brand-context.tsx`.
 */
export function useConvexOrgReady(orgId: string | null | undefined): boolean {
  return isConvexOrgReady(useConvexOrgId(), orgId)
}
